import { useQuery } from "@apollo/client";
import { max, min } from "lodash";
import { Suspense, useMemo } from "react";
import { useIntl } from "react-intl";

import { LoadingSkeleton } from "@joor/design-system";
import { useDateFormatted } from "lib/DateFormat";
import Suspender from "lib/Suspender";
import {
  WorkspaceDeliveryWindowAssortmentFragment,
  WorkspaceDeliveryWindowDoc,
} from "operations";
import {
  StyledContent,
  StyledLabel,
  StyledTitle,
  SummaryBarContent,
} from "./styledComponents";

const dateOpts = { timeZone: "UTC" };

const mapDeliveryWindow = (
  assortments: WorkspaceDeliveryWindowAssortmentFragment[]
): { completeShip: Date; startShip: Date } | null =>
  assortments.length
    ? assortments
        .map(({ deliveryWindow: dw }) => ({
          startShip: dw.startShip && new Date(dw.startShip),
          completeShip: dw.completeShip && new Date(dw.completeShip),
        }))
        .reduce((acc, dw) => ({
          startShip: min([acc.startShip, dw.startShip]),
          completeShip: max([acc.completeShip, dw.completeShip]),
        }))
    : null;

const useDeliveryWindow = (workspaceId: string) => {
  const intl = useIntl();
  const dateFormatted = useDateFormatted();
  const { data, loading } = useQuery(WorkspaceDeliveryWindowDoc, {
    variables: { workspaceId },
  });
  const deliveryWindow = useMemo(
    () => mapDeliveryWindow(data?.retailWorkspace?.assortments ?? []),
    [data]
  );
  const startShip =
    deliveryWindow?.startShip &&
    dateFormatted(intl.formatDate(deliveryWindow.startShip, dateOpts));
  const completeShip =
    deliveryWindow?.completeShip &&
    dateFormatted(intl.formatDate(deliveryWindow.completeShip, dateOpts));
  return {
    deliveryWindow:
      (completeShip || startShip) &&
      `${startShip ?? "TBD"} – ${completeShip ?? "TBD"}`,
    loading,
  };
};

export const DeliveryWindow = ({ workspaceId }: { workspaceId: string }) => {
  const { deliveryWindow, loading } = useDeliveryWindow(workspaceId);
  return (
    <SummaryBarContent>
      <StyledContent>
        <StyledLabel>Delivery Window</StyledLabel>
        <Suspense fallback={<LoadingSkeleton />}>
          <Suspender suspend={loading}>
            <StyledTitle data-testid="delivery-window-value">
              {deliveryWindow ?? "-"}
            </StyledTitle>
          </Suspender>
        </Suspense>
      </StyledContent>
    </SummaryBarContent>
  );
};
